/**
 * DemoContext – Global state for the demo orchestration system.
 *
 * Provides:
 *  - isPlaying / isPaused / currentStep / scenario / speed
 *  - Actions: play, pause, resume, stop, setScenario, setSpeed, nextStep, prevStep
 *
 * Only active when VITE_DEMO_MODE === 'true'.
 */
import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';
import { isDemoModeEnabled } from '@/modules/shared/services/demoFlowService';

// ─── Types ──────────────────────────────────────────────

export type DemoSpeed = 1 | 2 | 3;

export interface DemoScenario {
  id: string;
  name: string;
  description: string;
  programCode: string;
}

export interface DemoContextState {
  /** Demo mode enabled globally (env flag). */
  isDemoMode: boolean;
  /** Auto-play is running (not paused). */
  isPlaying: boolean;
  /** Auto-play is paused mid-flow. */
  isPaused: boolean;
  /** Index of the current step in the active script. */
  currentStep: number;
  /** Total steps in the active script. */
  totalSteps: number;
  /** Active flow/script id. */
  activeFlowId: string | null;
  /** Active demo scenario. */
  scenario: DemoScenario | null;
  /** Playback speed multiplier. */
  speed: DemoSpeed;
}

type DemoAction =
  | { type: 'PLAY'; flowId: string; totalSteps: number }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'STOP' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: number }
  | { type: 'SET_SPEED'; speed: DemoSpeed }
  | { type: 'SET_SCENARIO'; scenario: DemoScenario | null };

const INITIAL_STATE: DemoContextState = {
  isDemoMode: isDemoModeEnabled(),
  isPlaying: false,
  isPaused: false,
  currentStep: 0,
  totalSteps: 0,
  activeFlowId: null,
  scenario: null,
  speed: 1,
};

function demoReducer(state: DemoContextState, action: DemoAction): DemoContextState {
  switch (action.type) {
    case 'PLAY':
      return { ...state, isPlaying: true, isPaused: false, currentStep: 0, activeFlowId: action.flowId, totalSteps: action.totalSteps };
    case 'PAUSE':
      return { ...state, isPlaying: false, isPaused: true };
    case 'RESUME':
      return { ...state, isPlaying: true, isPaused: false };
    case 'STOP':
      return { ...state, isPlaying: false, isPaused: false, currentStep: 0, activeFlowId: null, totalSteps: 0 };
    case 'NEXT_STEP':
      return state.currentStep < state.totalSteps - 1
        ? { ...state, currentStep: state.currentStep + 1 }
        : { ...state, isPlaying: false, isPaused: false };
    case 'PREV_STEP':
      return state.currentStep > 0
        ? { ...state, currentStep: state.currentStep - 1 }
        : state;
    case 'GO_TO_STEP':
      return action.step >= 0 && action.step < state.totalSteps
        ? { ...state, currentStep: action.step }
        : state;
    case 'SET_SPEED':
      return { ...state, speed: action.speed };
    case 'SET_SCENARIO':
      return { ...state, scenario: action.scenario };
    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────

interface DemoContextValue extends DemoContextState {
  play: (flowId: string, totalSteps: number) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  setSpeed: (speed: DemoSpeed) => void;
  setScenario: (scenario: DemoScenario | null) => void;
}

const DemoContext = createContext<DemoContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────

export function DemoProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(demoReducer, INITIAL_STATE);

  const play = useCallback((flowId: string, totalSteps: number) => dispatch({ type: 'PLAY', flowId, totalSteps }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const resume = useCallback(() => dispatch({ type: 'RESUME' }), []);
  const stop = useCallback(() => dispatch({ type: 'STOP' }), []);
  const nextStep = useCallback(() => dispatch({ type: 'NEXT_STEP' }), []);
  const prevStep = useCallback(() => dispatch({ type: 'PREV_STEP' }), []);
  const goToStep = useCallback((step: number) => dispatch({ type: 'GO_TO_STEP', step }), []);
  const setSpeed = useCallback((speed: DemoSpeed) => dispatch({ type: 'SET_SPEED', speed }), []);
  const setScenario = useCallback((scenario: DemoScenario | null) => dispatch({ type: 'SET_SCENARIO', scenario }), []);

  const value = useMemo<DemoContextValue>(
    () => ({ ...state, play, pause, resume, stop, nextStep, prevStep, goToStep, setSpeed, setScenario }),
    [state, play, pause, resume, stop, nextStep, prevStep, goToStep, setSpeed, setScenario],
  );

  // Skip rendering provider entirely when demo mode is off
  if (!state.isDemoMode) return <>{children}</>;

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────

export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) {
    // Return a safe no-op stub when used outside provider (demo mode off)
    return {
      ...INITIAL_STATE,
      play: () => {},
      pause: () => {},
      resume: () => {},
      stop: () => {},
      nextStep: () => {},
      prevStep: () => {},
      goToStep: () => {},
      setSpeed: () => {},
      setScenario: () => {},
    };
  }
  return ctx;
}
